import * as PIXI from 'pixi.js';
import { ProcessSpectrum, GetSpectrumMagnitude } from '../../ProcessSpectrum';
import { CONSTANTS, PARTICLE_DIRECTIONS } from '../../PreviewDisplayConstants';
import { createMediaTexture, clearAllTextures, getDefaultTextures } from './textureUtils';
import { DEFAULT_PARTICLES_FLICKERING_SETTINGS, DEFAULT_PARTICLES_ROTATION_SETTINGS } from '../../../CreatePageConstants';

const baseHeight = 500;
const originZ = 100;
const baseSize = 10;
const baseMaxVisualSize = 60;
const minSpeed = 0.01;
const maxSpeed = 0.1;
const speedUpFactor = 600;
const flickerSpeedUpFactor = 100;
const rotationSpeedUpFactor = 100;
const minA = 0;
const maxA = 0.1;

const maxRerollAttempts = 20;

const minInsaneOscillation = 0.03;
const insaneDetectionThreshold = 10.87;

const minB = 20;
const maxB = 100;
const minH = 0;
const maxH = maxB * 2 * Math.PI;

export default class StarTravel {
    constructor(stage, zIndex) {
        this.stage = stage;

        const defaultTextures = getDefaultTextures();

        this.defaultCircleTexture1 = defaultTextures.circleTexture1;
        this.defaultCircleTexture2 = defaultTextures.circleTexture2;
        this.defaultHeartTexture1 = defaultTextures.heartTexture1;
        this.defaultHeartTexture2 = defaultTextures.heartTexture2;
        this.defaultStarTexture1 = defaultTextures.starTexture1;
        this.defaultStarTexture2 = defaultTextures.starTexture2;
        this.defaultNoteTexture1 = defaultTextures.noteTexture1;
        this.defaultNoteTexture2 = defaultTextures.noteTexture2;

        this.particles = [];
        this.particleTextures = [];

        this.container = new PIXI.Container();
        this.stage.addChild(this.container);

        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;

        this.calculatedWidth = (baseHeight * CONSTANTS.DEFAULT_WIDTH) / CONSTANTS.DEFAULT_HEIGHT;
        this.x = CONSTANTS.DEFAULT_WIDTH * 0.5;
        this.y = CONSTANTS.DEFAULT_HEIGHT * 0.5;
        this.container.x = this.x;
        this.container.y = this.y;
        this.container.scale.set(multiplier, multiplier);
        this.container.zIndex = zIndex;

        this.direction = PARTICLE_DIRECTIONS.OUT;

        this.fps = 60;
    }

    update(config) {
        if (this.enabled !== config.enabled) {
            this.container.alpha = config.enabled ? 1 : 0;
            if (!config.enabled) {
                this.particles.forEach(p => p.sprite.destroy());
                this.container.removeChildren();
                this.particles = [];
            }
        }

        this.enabled = config.enabled;
        this.spectrumOptions = config.spectrumOptions;
        this.speedUpEnabled = config.speedUpEnabled;

        const newParticleTextures = config.particleTextures || [];
        const texturesChanged = JSON.stringify(this.particleTextures || []) !== JSON.stringify(newParticleTextures);

        if (texturesChanged) {
            this.particles.forEach(p => p.sprite.destroy());
            this.container.removeChildren();
            this.particles = [];
        }

        this.particleTextures = newParticleTextures;
        this.activeParticles = this.particleTextures.filter(config => (config.birthRate || 0) > 0);
    }

    draw(spectrum) {
        if (!this.enabled) return;

        const processedSpectrum = ProcessSpectrum(spectrum, this.spectrumOptions);
        const spectrumMagnitude = GetSpectrumMagnitude(processedSpectrum);

        let speedMultiplier = this.speedUpEnabled ? (1 + (spectrumMagnitude / 255) * speedUpFactor) : 1;
        
        if (!this.activeParticles || this.activeParticles.length === 0) {
            speedMultiplier = this.fps === 30 ? speedMultiplier * 2 : speedMultiplier;
        } else {
            const totalBirthRate = this.activeParticles.reduce((sum, config) => sum + (config.birthRate || 35), 0);
            const averagedTotalBirthRate = totalBirthRate / this.activeParticles.length;
            this.activeParticles.forEach((particleConfig) => {
                const particleBirthRate = particleConfig.birthRate || 35;
                const proportionalBirthRate = (particleBirthRate / totalBirthRate) * averagedTotalBirthRate;
                this.addNewParticles(proportionalBirthRate * speedMultiplier, this.fps, particleConfig);
            });
 
            speedMultiplier = this.fps === 30 ? speedMultiplier * 2 : speedMultiplier;
        }
        
        const removalIndexes = [];
        const removalParticles = [];
        this.particles.forEach((e, i) => {
            const newZ = e.z + ((e.zSpeed * speedMultiplier) * ((originZ + e.z) / originZ));
            e.x += e.xSpeed * speedMultiplier + (e.aX * Math.sin((newZ - e.hX) / e.bX));
            e.y += e.ySpeed * speedMultiplier + (e.aY * Math.sin((newZ - e.hY) / e.bY));
            e.z = newZ
            const sprite = e.sprite;
            const convertedValues = this.convert3dto2d(e.x, e.y, e.z, e.size);
            sprite.x = convertedValues.x;
            sprite.y = convertedValues.y;
            const size = convertedValues.size > e.maxVisualSize ? e.maxVisualSize : convertedValues.size;
            sprite.width = size;
            sprite.height = size;

            if (e.flickeringEnabled) {
                const flickerSpeedMultiplier = e.reactive ? (1 + (spectrumMagnitude / 255) * flickerSpeedUpFactor) : 1;
                e.opacityPhase += e.opacitySpeed * flickerSpeedMultiplier;
                const normalizedSin = (Math.sin(e.opacityPhase) + 1) / 2;
                sprite.alpha = e.minOpacity + (e.maxOpacity - e.minOpacity) * normalizedSin;
            }

            if (e.rotationEnabled) {
                const rotationMultiplier = this.speedUpEnabled ? (1 + (spectrumMagnitude / 255) * rotationSpeedUpFactor) : 1;
                sprite.rotation += e.rotationSpeed * rotationMultiplier;
            }

            if (Math.abs(sprite.x) - sprite.width > this.calculatedWidth * 0.5 || Math.abs(sprite.y) - sprite.height > baseHeight * 0.5) {
                removalParticles.unshift(e.sprite)
                removalIndexes.unshift(i);
            }
        });
        removalParticles.forEach((sprite) => sprite.destroy());
        
        removalIndexes.forEach(e => this.particles.splice(e, 1));
    }

    createMovement() {
        const roll = () => ({
            xSpeed: this.randomValue(0, maxSpeed, true),
            ySpeed: this.randomValue(0, maxSpeed, true),
            aX: this.randomValue(minA, maxA),
            aY: this.randomValue(minA, maxA),
            zSpeed: this.randomValue(minSpeed, maxSpeed),
            bX: this.randomValue(minB, maxB),
            bY: this.randomValue(minB, maxB)
        });

        let movement = roll();

        for (let attempt = 0; attempt < maxRerollAttempts && this.isInsane(movement); attempt++) {
            movement = roll();
        }

        return movement;
    }

    isInsane(movement) {
        if (movement.aX < minInsaneOscillation && movement.aY < minInsaneOscillation) return false;

        const oscillation = Math.hypot(movement.aX, movement.aY);
        return oscillation > Math.hypot(movement.xSpeed, movement.ySpeed) * insaneDetectionThreshold;
    }

    addNewParticles(birthRate, fps, particleConfig) {  
        let n = birthRate / fps;
        let diff = n - Math.floor(n);
        n = Math.floor(n);
        if (Math.random() < diff) {
            n++;
        }

        const parsedConfig = {
            mediaData: particleConfig?.mediaData,
            shape: particleConfig?.shape,
            tint: particleConfig?.color ? parseInt(particleConfig.color) : 0xffffff,
            minOpacity: particleConfig?.minOpacity ?? 0.2,
            maxOpacity: particleConfig?.maxOpacity ?? 1,
            minSize: particleConfig?.minSize ?? 0.1,
            maxSize: particleConfig?.maxSize ?? 0.3,
            flickeringEnabled: particleConfig?.flickering?.enabled ?? DEFAULT_PARTICLES_FLICKERING_SETTINGS.enabled,
            minFlickeringIntensity: particleConfig?.flickering?.minIntensity ?? DEFAULT_PARTICLES_FLICKERING_SETTINGS.minIntensity,
            maxFlickeringIntensity: particleConfig?.flickering?.maxIntensity ?? DEFAULT_PARTICLES_FLICKERING_SETTINGS.maxIntensity,
            reactive: particleConfig?.flickering?.reactive ?? DEFAULT_PARTICLES_FLICKERING_SETTINGS.reactive,
            rotationEnabled: particleConfig?.rotationEnabled ?? DEFAULT_PARTICLES_ROTATION_SETTINGS.enabled,
            minRotationSpeed: DEFAULT_PARTICLES_ROTATION_SETTINGS.minSpeed,
            maxRotationSpeed: DEFAULT_PARTICLES_ROTATION_SETTINGS.maxSpeed
        };

        for (let i = 0; i < n; i++) {
            const newParticle = this.createParticle(parsedConfig);
            this.particles.push(newParticle);
            this.container.addChild(newParticle.sprite);
        }
    }

    createParticle(parsedConfig) {
        let texture;

        if (parsedConfig.mediaData) {
            texture = createMediaTexture(parsedConfig.shape, parsedConfig.mediaData);
        } else if (parsedConfig.shape === 'heart') {
            texture = Math.random() < 0.5 ? this.defaultHeartTexture1 : this.defaultHeartTexture2;
        } else if (parsedConfig.shape === 'star') {
            texture = Math.random() < 0.5 ? this.defaultStarTexture1 : this.defaultStarTexture2;
        } else if (parsedConfig.shape === 'note') {
            texture = Math.random() < 0.5 ? this.defaultNoteTexture1 : this.defaultNoteTexture2;
        } else {
            texture = Math.random() < 0.5 ? this.defaultCircleTexture1 : this.defaultCircleTexture2;
        }

        const sprite = new PIXI.Sprite(texture);

        sprite.tint = parsedConfig.tint;
        sprite.anchor.set(0.5);
        sprite.alpha = this.randomValue(parsedConfig.minOpacity, parsedConfig.maxOpacity);
        sprite.width = this.randomValue(baseSize * parsedConfig.minSize, baseSize * parsedConfig.maxSize);
        sprite.height = sprite.width;
        sprite.rotation = parsedConfig.rotationEnabled ? Math.random() * Math.PI * 2 : 0;

        const movement = this.createMovement();

        return {
            sprite: sprite,
            x: 0,
            y: 0,
            z: originZ,
            xSpeed: movement.xSpeed,
            ySpeed: movement.ySpeed,
            zSpeed: movement.zSpeed,
            size: this.randomValue(baseSize * parsedConfig.minSize, baseSize * parsedConfig.maxSize),
            maxVisualSize: baseMaxVisualSize * parsedConfig.maxSize,
            aX: movement.aX,
            bX: movement.bX,
            hX: this.randomValue(minH, maxH),
            aY: movement.aY,
            bY: movement.bY,
            hY: this.randomValue(minH, maxH),
            flickeringEnabled: parsedConfig.flickeringEnabled,
            reactive: parsedConfig.reactive,
            minOpacity: parsedConfig.minOpacity,
            maxOpacity: parsedConfig.maxOpacity,
            opacityPhase: Math.random() * Math.PI * 2,
            opacitySpeed: this.randomValue(parsedConfig.minFlickeringIntensity, parsedConfig.maxFlickeringIntensity),
            rotationEnabled: parsedConfig.rotationEnabled,
            rotationSpeed: parsedConfig.rotationEnabled ? this.randomValue(parsedConfig.minRotationSpeed, parsedConfig.maxRotationSpeed, true) : 0,
        }
    }

    convert3dto2d(x, y, z, size) {
        return {
            x: x / (originZ / z),
            y: y / (originZ / z),
            size: (size * z) / originZ
        }
    }

    randomValue(min, max, negative = false) {
        let result = Math.random() * (max - min) + min;
        if (negative) {
            result = Math.random() < 0.5 ? result : -result;
        }
        return result;
    }

    updateStage(stage) {
        this.stage = stage;
        this.stage.addChild(this.container);
    }

    destroy() {
        this.container.destroy({ children: true });
        this.particles = [];
        clearAllTextures();
    }
}